// Copyright (C) 2026, Lifefinder Systems International AB

import React, { useCallback, useEffect, useState } from 'react';

import type { AreaShape, CameraLock } from '../../../types';
import { cameraLockAPI, userAPI } from '../../../utils/apiClient';
import logger from '../../../utils/logger';

// ---------------------------------------------------------------------------
// Styles (kept in step with the geofence AreaEditor panel)
// ---------------------------------------------------------------------------

const panelStyle: React.CSSProperties = {
  position: 'fixed',
  top: '10vh',
  right: '1vmin',
  width: 'clamp(260px, 22vw, 360px)',
  maxHeight: '80vh',
  overflowY: 'auto',
  background: '#fff',
  borderRadius: '8px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
  zIndex: 1700,
  padding: '12px',
  fontFamily: 'sans-serif',
  fontSize: '14px',
};

const btnStyle: React.CSSProperties = {
  padding: '4px 10px',
  borderRadius: '4px',
  border: '1px solid #aaa',
  background: '#f5f5f5',
  cursor: 'pointer',
  fontSize: '13px',
};

const btnPrimaryStyle: React.CSSProperties = {
  ...btnStyle,
  background: '#16a34a',
  color: '#fff',
  border: '1px solid #15803d',
};

const btnActiveStyle: React.CSSProperties = {
  ...btnStyle,
  background: '#2563eb',
  color: '#fff',
  border: '1px solid #1d4ed8',
};

const btnDangerStyle: React.CSSProperties = {
  ...btnStyle,
  color: '#c00',
  borderColor: '#c00',
};

const btnLinkStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#2563eb',
  cursor: 'pointer',
  fontSize: '12px',
  padding: 0,
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  gap: '6px',
  marginTop: '10px',
  flexWrap: 'wrap',
};

const userListStyle: React.CSSProperties = {
  marginTop: '4px',
  maxHeight: '30vh',
  overflowY: 'auto',
  border: '1px solid #aaa',
  borderRadius: '4px',
  padding: '4px',
};

const userItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '3px 4px',
  cursor: 'pointer',
};

// Allowed zoom-out steps beyond the zoom level the area was drawn at.
const ALLOWANCE_OPTIONS = [0, 1, 2];
const DEFAULT_ALLOWANCE = 1;

interface ManageableUser {
  id: string;
  username: string;
  rights: string;
}

export interface CameraLockEditorProps {
  /** True while the user is actively drawing the rectangle on the map. */
  drawing: boolean;
  /** Shape captured after the rectangle is drawn (null until then). */
  pendingShape: AreaShape | null;
  /** Map zoom level at the moment the rectangle was completed. */
  drawZoom: number | null;
  onStartDraw: () => void;
  onDiscardShape: () => void;
  onClose: () => void;
}

/**
 * SuperAdmin panel for configuring the per-user timespace camera lock. The
 * SuperAdmin picks one or more target users (Admin and below — SuperAdmins are
 * exempt and cannot be locked), then draws/enables/clears the lock. The drawn
 * rectangle is applied to every selected user at once. When exactly one user is
 * selected, that user's current lock status plus enable/disable/remove controls
 * are shown. The editing SuperAdmin's own view is never affected.
 */
const CameraLockEditor: React.FC<CameraLockEditorProps> = ({
  drawing,
  pendingShape,
  drawZoom,
  onStartDraw,
  onDiscardShape,
  onClose,
}) => {
  const [users, setUsers] = useState<ManageableUser[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [targetLock, setTargetLock] = useState<CameraLock | null>(null);
  const [allowance, setAllowance] = useState<number>(DEFAULT_ALLOWANCE);
  const [saving, setSaving] = useState(false);
  const [loadingLock, setLoadingLock] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load the users this SuperAdmin can lock (exclude other SuperAdmins — they're exempt).
  useEffect(() => {
    (async () => {
      try {
        const list = await userAPI.getUsers();
        const lockable = (list as ManageableUser[]).filter((u) => !(u.rights || '').includes('S'));
        setUsers(lockable);
      } catch (err) {
        logger.error({ err }, 'Failed to load users for camera lock');
        setError('Could not load users.');
      }
    })();
  }, []);

  // Fetch a single user's existing lock to pre-populate status.
  const loadTargetLock = useCallback(async (userId: string) => {
    setLoadingLock(true);
    setError(null);
    try {
      const res = await cameraLockAPI.getForUser(userId);
      setTargetLock(res.cameraLock);
    } catch (err) {
      logger.error({ err }, 'Failed to load camera lock for user');
      setError('Could not load this user’s lock.');
      setTargetLock(null);
    } finally {
      setLoadingLock(false);
    }
  }, []);

  // Per-user status/toggle/remove only make sense for a single target. Load that
  // user's lock when exactly one is selected; otherwise there's no single status.
  const singleUserId = selectedUserIds.length === 1 ? selectedUserIds[0] : null;
  useEffect(() => {
    if (singleUserId) {
      loadTargetLock(singleUserId);
    } else {
      setTargetLock(null);
    }
  }, [singleUserId, loadTargetLock]);

  // Selecting/deselecting a user invalidates any half-drawn rectangle.
  const handleToggleUser = (userId: string): void => {
    onDiscardShape();
    setSelectedUserIds((cur) =>
      cur.includes(userId) ? cur.filter((id) => id !== userId) : [...cur, userId]
    );
  };

  const handleSelectAll = (): void => {
    onDiscardShape();
    setSelectedUserIds(users.map((u) => u.id));
  };

  const handleClearSelection = (): void => {
    onDiscardShape();
    setSelectedUserIds([]);
  };

  const hasExistingShape = targetLock?.shape != null;
  const selectedCount = selectedUserIds.length;

  // Min zoom = draw zoom minus the allowed zoom-out steps, clamped to Leaflet's range.
  const computedMinZoom =
    drawZoom != null ? Math.max(1, Math.min(20, Math.round(drawZoom) - allowance)) : null;

  /** Runs an async per-user camera-lock call across all selected users, reporting partial failures. */
  const applyToSelected = async (
    op: (userId: string) => Promise<{ cameraLock: CameraLock }>,
    failureNoun: string
  ): Promise<void> => {
    setError(null);
    setSaving(true);
    try {
      const results = await Promise.allSettled(selectedUserIds.map((id) => op(id)));
      const failures = results.filter((r) => r.status === 'rejected');
      if (failures.length > 0) {
        logger.error({ failures }, `Failed to ${failureNoun} camera lock for some users`);
        setError(
          `Could not ${failureNoun} the lock for ${failures.length} of ${selectedCount} user(s).`
        );
      }
      // Refresh the single-user status from its own successful result, if applicable.
      if (singleUserId && results[0]?.status === 'fulfilled') {
        setTargetLock(results[0].value.cameraLock);
      }
      onDiscardShape();
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async (): Promise<void> => {
    if (selectedCount === 0 || !pendingShape || computedMinZoom == null) return;
    await applyToSelected(
      (id) => cameraLockAPI.setForUser(id, true, pendingShape, computedMinZoom),
      'save'
    );
  };

  const handleToggleEnabled = async (): Promise<void> => {
    if (!singleUserId || !hasExistingShape || !targetLock?.shape || targetLock.minZoom == null)
      return;
    setError(null);
    setSaving(true);
    try {
      const res = await cameraLockAPI.setForUser(
        singleUserId,
        !targetLock.enabled,
        targetLock.shape,
        targetLock.minZoom
      );
      setTargetLock(res.cameraLock);
    } catch (err) {
      logger.error({ err }, 'Failed to toggle camera lock');
      setError('Could not change the camera lock.');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async (): Promise<void> => {
    if (selectedCount === 0) return;
    await applyToSelected((id) => cameraLockAPI.clearForUser(id), 'remove');
  };

  return (
    <div style={panelStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <strong style={{ fontSize: '15px' }}>Camera lock</strong>
        <button style={btnStyle} onClick={onClose} title="Close">
          ✕
        </button>
      </div>

      <p style={{ color: '#666', margin: '8px 0', lineHeight: 1.4 }}>
        Lock users’ timespace map to a rectangle. They cannot pan outside the area or zoom out
        further than the chosen level. Select one or more users; the drawn area applies to all of
        them. SuperAdmins are never locked.
      </p>

      {/* Target users */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={{ color: '#555' }}>Users to lock:</span>
        <span style={{ color: '#777', fontSize: '12px' }}>
          <button style={btnLinkStyle} onClick={handleSelectAll} disabled={saving}>
            Select all
          </button>
          {' · '}
          <button style={btnLinkStyle} onClick={handleClearSelection} disabled={saving}>
            Clear
          </button>
        </span>
      </div>
      <div style={userListStyle} role="group" aria-label="Users to lock">
        {users.length === 0 && <div style={{ color: '#999', padding: '4px' }}>No users.</div>}
        {users.map((u) => (
          <label key={u.id} style={userItemStyle}>
            <input
              type="checkbox"
              checked={selectedUserIds.includes(u.id)}
              onChange={() => handleToggleUser(u.id)}
            />
            {u.username}
          </label>
        ))}
      </div>
      {selectedCount > 0 && (
        <div style={{ color: '#555', marginTop: '4px', fontSize: '12px' }}>
          {selectedCount} user{selectedCount === 1 ? '' : 's'} selected.
        </div>
      )}

      {selectedCount > 0 && (
        <>
          {/* Current status — only meaningful for a single selected user */}
          {singleUserId && (
            <div
              style={{
                background: '#f9f9f9',
                border: '1px solid #eee',
                borderRadius: '4px',
                padding: '8px',
                margin: '8px 0',
              }}
            >
              <div>
                Status:{' '}
                <strong style={{ color: targetLock?.enabled ? '#16a34a' : '#999' }}>
                  {loadingLock
                    ? 'Loading…'
                    : targetLock?.enabled
                      ? 'Active'
                      : hasExistingShape
                        ? 'Disabled'
                        : 'Not set'}
                </strong>
              </div>
              {hasExistingShape && targetLock?.minZoom != null && (
                <div style={{ color: '#555', marginTop: '2px' }}>
                  Minimum zoom: {targetLock.minZoom}
                </div>
              )}
            </div>
          )}

          {/* Draw control */}
          {!pendingShape && (
            <button
              style={drawing ? btnActiveStyle : btnStyle}
              onClick={onStartDraw}
              disabled={saving}
            >
              {drawing
                ? 'Click two corners on the map…'
                : singleUserId && hasExistingShape
                  ? 'Redraw area'
                  : 'Draw area'}
            </button>
          )}

          {/* Pending shape — choose allowance and save */}
          {pendingShape && (
            <div>
              <div style={{ color: '#16a34a', marginBottom: '6px' }}>
                Area drawn{drawZoom != null ? ` at zoom ${Math.round(drawZoom)}` : ''}.
              </div>
              <label style={{ display: 'block', color: '#555', marginBottom: '4px' }}>
                Allowed zoom-out (steps):
              </label>
              <div style={{ display: 'flex', gap: '6px' }}>
                {ALLOWANCE_OPTIONS.map((opt) => (
                  <button
                    key={opt}
                    style={allowance === opt ? btnActiveStyle : btnStyle}
                    onClick={() => setAllowance(opt)}
                  >
                    {opt}
                  </button>
                ))}
              </div>
              {computedMinZoom != null && (
                <div style={{ color: '#777', marginTop: '6px', fontSize: '12px' }}>
                  Minimum zoom will be {computedMinZoom}.
                </div>
              )}
              <div style={rowStyle}>
                <button style={btnPrimaryStyle} onClick={handleSave} disabled={saving}>
                  {saving
                    ? 'Saving…'
                    : selectedCount > 1
                      ? `Save & enable for ${selectedCount} users`
                      : 'Save & enable'}
                </button>
                <button style={btnStyle} onClick={onDiscardShape} disabled={saving}>
                  Discard drawing
                </button>
              </div>
            </div>
          )}

          {/* Enable/disable + clear for an already-saved single-user lock */}
          {singleUserId && hasExistingShape && !pendingShape && (
            <div style={rowStyle}>
              <button style={btnStyle} onClick={handleToggleEnabled} disabled={saving}>
                {targetLock?.enabled ? 'Disable' : 'Enable'}
              </button>
              <button style={btnDangerStyle} onClick={handleClear} disabled={saving}>
                Remove lock
              </button>
            </div>
          )}

          {/* Bulk clear for multiple selected users */}
          {selectedCount > 1 && !pendingShape && (
            <div style={rowStyle}>
              <button style={btnDangerStyle} onClick={handleClear} disabled={saving}>
                Remove lock from {selectedCount} users
              </button>
            </div>
          )}
        </>
      )}

      {error && <p style={{ color: '#c00', marginTop: '8px' }}>{error}</p>}
    </div>
  );
};

export default CameraLockEditor;
